import { useCallback, useEffect, useRef, useState } from 'react'
import { getHack } from '@domain/features/pd-hacks/pd-hacks-feature'
import type { HackSymptom } from '@domain/models/pd-hacks'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import {
  detailEntityMatchesRoute,
  isDetailLoadErrorFatal,
  keepDetailEntityForRoute,
} from './detail-load-state'

function matchesHackRoute(hack: HackSymptom, symptomId: string): boolean {
  return hack.uid === symptomId
}

export function useHack(symptomId: string | undefined) {
  const ssrHack = useSsrPageData()?.hackDetail?.hack ?? null
  const hasMatchingSsrHack = detailEntityMatchesRoute(
    ssrHack,
    symptomId,
    matchesHackRoute,
  )
  const [hack, setHackState] = useState<HackSymptom | null>(() =>
    keepDetailEntityForRoute(ssrHack, symptomId, matchesHackRoute))
  const [loading, setLoading] = useState(!hasMatchingSsrHack)
  const [error, setError] = useState<string | null>(null)
  const hackRef = useRef<HackSymptom | null>(hack)

  const setHack = useCallback((nextHack: HackSymptom | null) => {
    hackRef.current = nextHack
    setHackState(nextHack)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      await Promise.resolve()
      if (cancelled) {
        return
      }

      if (!symptomId) {
        setLoading(false)
        setHack(null)
        setError('Hack id is missing.')
        return
      }

      const resolvedSymptomId = symptomId
      const ssrFallback = keepDetailEntityForRoute(
        ssrHack,
        resolvedSymptomId,
        matchesHackRoute,
      )
      if (ssrFallback && !detailEntityMatchesRoute(
        hackRef.current,
        resolvedSymptomId,
        matchesHackRoute,
      )) {
        setHack(ssrFallback)
      } else if (!detailEntityMatchesRoute(
        hackRef.current,
        resolvedSymptomId,
        matchesHackRoute,
      )) {
        setHack(null)
      }

      try {
        if (!detailEntityMatchesRoute(
          hackRef.current,
          resolvedSymptomId,
          matchesHackRoute,
        )) {
          setLoading(true)
        }
        setError(null)
        const result = await getHack(resolvedSymptomId)
        if (!cancelled) {
          setHack(result)
        }
      } catch (loadError) {
        if (!cancelled && isDetailLoadErrorFatal(
          hackRef.current,
          resolvedSymptomId,
          matchesHackRoute,
        )) {
          setError(
            loadError instanceof Error ? loadError.message : 'Failed to load hack.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [setHack, ssrHack, symptomId])

  return { hack, loading, error }
}
